<section class="billing" aria-labelledby="billing-heading">
    <div class="billing-header">
        <div>
            <h2 id="billing-heading">Plan and billing</h2>
            <p>Choose the right plan for your team, top up PAYG credits or manage your subscription.</p>
        </div>
        <button mat-icon-button type="button" aria-label="Refresh billing details" (click)="refresh()" [disabled]="loading || working">
            <mat-icon>refresh</mat-icon>
        </button>
    </div>

    @if (error) {
        <div class="billing-message billing-error" role="alert">{{ error }}</div>
    }

    @if (planState?.billing_managed_externally) {
        <div class="billing-message" role="status">Your organisation pays by bank transfer. Contact us to change your plan or purchased surveyor seats.</div>
    }

    @if (accessUnavailable) {
        <div class="billing-message" role="status">This feature is not available with your current access. Review your plan below or ask an administrator; refresh billing details if your plan was just changed.</div>
    }

    @if (checkoutReturnMessage) {
        <div class="billing-message" [class.billing-error]="checkoutReturnError" [attr.role]="checkoutReturnError ? 'alert' : 'status'">{{ checkoutReturnMessage }}</div>
    }

    @if (checkoutUrl) {
        <a mat-stroked-button [href]="checkoutUrl" target="_blank" rel="noopener noreferrer">Open secure Stripe Checkout</a>
    }

    @if (loading) {
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    } @else {
        @if (setupOffer) {
            <div class="billing-message" role="status">
                <p>Complete your Pro setup. Your subscription starts after payment.</p>
                <button mat-flat-button color="primary" type="button" (click)="completeProSetup()" [disabled]="working">Continue to payment</button>
            </div>
        }
        @if (currentPlan === 'grandfathered_payg') {
            <app-alert tone="info" title="Grandfathered PAYG rates">
                @if (policy?.pricing_transition?.effective_at || catalogue?.grandfathering_ends_at) {
                    Your PAYG rates are protected until {{ (policy?.pricing_transition?.effective_at || catalogue?.grandfathering_ends_at) | date:'longDate' }}. After that, standard PAYG rates apply unless you move to a subscription.
                } @else {
                    Your account is on grandfathered PAYG rates.
                }
            </app-alert>
        }

        <div class="billing-overview" [class.has-billing-summary]="showBillingSummary">
        <div class="billing-grid">
            <mat-card>
                <mat-card-header><mat-card-title>Current plan</mat-card-title></mat-card-header>
                <mat-card-content>
                    <div class="current-plan-heading">
                        <div class="billing-value">{{ currentPlanLabel }}</div>
                        @if (currentPlan) {
                            <span class="active-plan-pill"><mat-icon>check</mat-icon>Active</span>
                        }
                    </div>

                    @if (isPaygPlan) {
                        <div class="credit-balance">
                            <strong>{{ creditBalance === null ? 'Unavailable' : (creditBalance | number) }}</strong>
                            <span>{{ creditBalance === 1 ? 'credit available' : 'credits available' }}</span>
                            <button mat-stroked-button type="button" (click)="openCreditPacks()" [disabled]="planState?.billing_managed_externally">Buy credits</button>
                        </div>
                    } @else if (isProPlan && proBenefits.length) {
                        <div class="current-plan-benefits" aria-label="Included with Pro">
                            @for (benefit of proBenefits; track benefit) {
                                <span><mat-icon>check</mat-icon>{{ benefit }}</span>
                            }
                        </div>
                    } @else if (policy?.unlimited_pins === true) {
                        <p class="plan-benefit">Unlimited surveys included</p>
                    }

                    @if (subscriptionDateLabel && subscriptionDate) {
                        <div class="current-plan-date">
                            <span>{{ subscriptionDateLabel }}</span>
                            <strong>{{ subscriptionDate | date:'longDate' }}</strong>
                        </div>
                    }

                    @if (isProPlan && planState?.subscription && !planState?.billing_managed_externally) {
                        <div class="subscription-actions">
                            @if (planState.subscription.renewal_enabled === false) {
                                <button mat-stroked-button color="primary" type="button" (click)="resumeSubscription()" [disabled]="working">Resume subscription</button>
                            } @else {
                                <button mat-stroked-button color="warn" type="button" (click)="confirmSubscriptionCancellation()" [disabled]="working">Cancel subscription</button>
                            }
                        </div>
                    }

                    @if (policy?.status === 'unresolved') {
                        <p class="billing-warning">Access could not be confirmed. Refresh or ask an organisation administrator; unresolved policy is never treated as unlimited.</p>
                    }
                </mat-card-content>
            </mat-card>

            @if (isProPlan) {
                <mat-card class="alternative-plan-card">
                    <mat-card-header><mat-card-title>Pay as you go</mat-card-title></mat-card-header>
                    <mat-card-content>
                        <p>Pay for credits when you need them, with no recurring plan fee.</p>
                        <div class="alternative-plan-benefits">
                            <span><mat-icon>check</mat-icon>No monthly subscription</span>
                            <span><mat-icon>check</mat-icon>Top up credits when needed</span>
                        </div>

                        @if (planState?.subscription?.renewal_enabled === false) {
                            <p class="billing-message">
                                PAYG is scheduled to begin when Pro access ends
                                @if (subscriptionDate) { on {{ subscriptionDate | date:'longDate' }} }.
                            </p>
                        }

                        <button mat-stroked-button type="button" (click)="choosePlan('payg')" [disabled]="working || planState?.billing_managed_externally">Switch to PAYG</button>
                    </mat-card-content>
                </mat-card>
            } @else {
                <mat-card>
                    <mat-card-header><mat-card-title>Pro</mat-card-title></mat-card-header>
                    <mat-card-content>
                        <p>More tools for teams that want to survey and manage their work without credits.</p>

                        @if (proBenefits.length) {
                            <div class="pro-benefits">
                                @for (benefit of proBenefits; track benefit) {
                                    <span><mat-icon>check</mat-icon>{{ benefit }}</span>
                                }
                            </div>
                        }

                        <div class="offer-grid pro-offers">
                            @for (offer of baseOffers; track offer.price_id) {
                                <button class="offer-card" type="button" (click)="choosePlan(offer.interval === 'year' ? 'pro_annual' : 'pro_monthly')" [disabled]="working || planState?.billing_managed_externally">
                                    <span class="offer-name">Pro {{ offer.interval === 'year' ? 'Annual' : 'Monthly' }}</span>
                                    <strong>{{ money(offer.amount, offer.currency) }}</strong>
                                    <span class="offer-frequency">per {{ offer.interval === 'year' ? 'year' : 'month' }} · {{ taxLabel(offer.tax_behavior) }}</span>

                                    @if (offer.interval === 'year' && annualBaseSaving) {
                                        <span class="offer-saving">Save {{ money(annualBaseSaving, offer.currency) }} on the annual base</span>
                                    }

                                    <span class="offer-action">Choose {{ offer.interval === 'year' ? 'annual' : 'monthly' }}</span>
                                </button>
                            }
                        </div>

                        @if (proSeatOffer) {
                            <p class="billing-fine-print">Additional surveyor seats cost {{ money(proSeatOffer.amount, proSeatOffer.currency) }} each per month, billed monthly even with annual Pro.</p>
                        } @else {
                            <p class="billing-fine-print">Additional surveyor seat pricing is temporarily unavailable.</p>
                        }
                    </mat-card-content>
                </mat-card>
            }
        </div>

        @if (showBillingSummary && surveyors) {
            <aside class="billing-summary" aria-label="Billing summary">
                <mat-card class="billing-section">
                    <mat-card-header><mat-card-title>Billing summary</mat-card-title></mat-card-header>
                    <mat-card-content>
                        <div class="billing-summary-item">
                            <div>
                                <strong>Pro {{ baseBillingInterval === 'year' ? 'annual' : 'monthly' }}</strong>
                                @if (subscriptionDateLabel && subscriptionDate) {
                                    <span>{{ subscriptionDateLabel }} · {{ subscriptionDate | date:'longDate' }}</span>
                                }
                            </div>

                            <div class="billing-summary-price">
                                <strong>{{ money(surveyors.base?.amount, surveyors.currency) }}</strong>
                                <span>per {{ baseBillingInterval }}</span>
                            </div>
                        </div>

                        @if (extraSurveyorSeats > 0) {
                            <div class="billing-summary-item">
                                <div>
                                    <strong>{{ extraSurveyorSeats }} purchased surveyor {{ extraSurveyorSeats === 1 ? 'seat' : 'seats' }}</strong>

                                    @if (seatBillingDate) {
                                        <span>{{ seatBillingDateLabel }} · {{ seatBillingDate | date:'longDate' }}</span>
                                    }

                                    @if ((surveyors.reduction_at_renewal ?? 0) > 0) {
                                        <span>{{ surveyors.reduction_at_renewal }} {{ surveyors.reduction_at_renewal === 1 ? 'seat ends' : 'seats end' }} at renewal</span>
                                    }
                                </div>

                                <div class="billing-summary-price">
                                    <strong>{{ money(surveyors.ongoing_monthly_amount, surveyors.currency) }}</strong>
                                    <span>per month</span>
                                </div>
                            </div>
                        }

                        <div class="billing-summary-total">
                            <span>Estimated total due</span>
                            @if (invoicePreviewLoading) {
                                <strong>Checking…</strong>
                            } @else {
                                <strong>{{ upcomingPaymentTotal === null ? 'Unavailable' : money(upcomingPaymentTotal, surveyors.currency) }}</strong>
                            }
                            @if (invoicePreviewError) {
                                <small>Refresh to check your upcoming payments.</small>
                            } @else {
                                <small>Includes discounts, applicable tax and account credit. Charged on the billing dates above.</small>
                            }
                        </div>
                    </mat-card-content>
                </mat-card>
            </aside>
        }
        </div>

        @if (surveyors && surveyors.status !== 'not_applicable') {
            <div class="billing-details-grid">
                <mat-card class="billing-section">
                    <mat-card-header><mat-card-title>Surveyor seats</mat-card-title></mat-card-header>
                    <mat-card-content>
                        @if (surveyors.status === 'ready') {
                            <p>Active and invited App-only or Web-and-app users need a surveyor seat. Removing App access frees it for reuse without cancelling it.</p>
                            <p>Purchased surveyor seats bill monthly in advance, even with annual Pro. Cancel an unassigned surveyor seat at its next monthly renewal, with no automatic mid-period refund or credit. You can undo cancellation before renewal.</p>
                            <p>{{ surveyors.included_licences ?? surveyors.included_surveyors }} included · {{ surveyors.purchased_licences ?? extraSurveyorSeats }} purchased</p>

                            <div class="seat-summary">
                                <div class="surveyor-usage-breakdown">
                                    <div class="seat-usage"><strong>{{ surveyors.assigned_licences ?? surveyors.active_surveyors }}</strong><span>of {{ assignableSurveyorSeats }} surveyor seats in use</span></div>
                                    <div class="seat-usage additional-surveyors"><strong>{{ surveyors.available_licences ?? availableSurveyorSeats }}</strong><span>available</span></div>
                                </div>

                                <div class="seat-summary-actions">
                                    @if (seatOffer) { <span>{{ money(seatOffer.amount, seatOffer.currency) }} per additional surveyor seat/month</span> }
                                    <button mat-raised-button color="primary" type="button" (click)="buyLicence()" [disabled]="planState?.billing_managed_externally || working || !seatOffer"><mat-icon>add_card</mat-icon>Buy surveyor seats</button>
                                </div>
                            </div>

                            @if (licenceError) {
                                <p class="billing-warning" role="alert">{{ licenceError }}</p>
                            } @else if (licences.length) {
                                <div class="surveyor-contributors">
                                    <h3>Surveyor seat details</h3>
                                    <div class="licence-table-wrap">
                                        <table class="licence-table">
                                            <thead>
                                                <tr><th>Surveyor seat</th><th>Purchase / origin</th><th>Assigned to</th><th>Status</th><th><span class="visually-hidden">Actions</span></th></tr>
                                            </thead>
                                            <tbody>
                                                @for (licence of visibleLicences; track licence.id) {
                                                    <tr>
                                                        <td>{{ licence.identifier ?? '—' }}</td>
                                                        <td>
                                                            @if (licence.source === 'purchased') {
                                                                {{ licence.created_at | date:'mediumDate' }}
                                                            } @else if (licence.source === 'included') {
                                                                Included with plan
                                                            } @else {
                                                                Existing App access
                                                            }
                                                        </td>
                                                        <td>
                                                            @if (licence.user) {
                                                                <strong>{{ licence.user.name }}</strong><small>{{ licence.user.email }}</small>
                                                            } @else {
                                                                <span class="licence-unassigned">Unassigned</span>
                                                            }
                                                        </td>
                                                        <td>
                                                            @if (licence.status === 'cancellation_pending') {
                                                                <span class="active-plan-pill licence-status-pending"><mat-icon>schedule</mat-icon>Ends at renewal</span>
                                                            } @else {
                                                                <span class="active-plan-pill"><mat-icon>check</mat-icon>Active</span>
                                                            }
                                                        </td>
                                                        <td class="licence-actions">
                                                            <button mat-icon-button type="button" [matMenuTriggerFor]="licenceMenu" [disabled]="working" [attr.aria-label]="'Actions for surveyor seat ' + (licence.identifier ?? '')"><mat-icon>more_vert</mat-icon></button>
                                                            <mat-menu #licenceMenu="matMenu">
                                                                @if (licence.status === 'cancellation_pending') {
                                                                    <button mat-menu-item type="button" (click)="resumeLicence(licence)" [disabled]="planState?.billing_managed_externally" *appCan="'manage_billing'">Keep seat</button>
                                                                } @else {
                                                                    <ng-container *appCan="'manage_billing'">
                                                                        <button mat-menu-item type="button" (click)="openLicenceAssignment(licence)">{{ licence.user ? 'Reassign' : 'Assign' }}</button>
                                                                        @if (licence.user) {
                                                                            <button mat-menu-item type="button" (click)="unassignLicence(licence)">Unassign</button>
                                                                        }
                                                                        @if (licence.source === 'purchased' && !licence.user) {
                                                                            <button mat-menu-item type="button" (click)="cancelLicence(licence)" [disabled]="planState?.billing_managed_externally">Cancel seat</button>
                                                                        }
                                                                    </ng-container>
                                                                }
                                                            </mat-menu>
                                                        </td>
                                                    </tr>
                                                }
                                            </tbody>
                                        </table>
                                    </div>

                                    @if (hiddenLicenceCount > 0 || showAllLicences) {
                                        <button mat-button type="button" (click)="toggleLicenceList()">
                                            {{ showAllLicences ? 'Show less' : 'See more (' + hiddenLicenceCount + ')' }}
                                        </button>
                                    }
                                </div>
                            } @else {
                                <p class="seat-caption">No surveyor seats are available yet.</p>
                            }

                        } @else {
                            <p class="billing-warning">Surveyor seat information is temporarily unavailable. Refresh or manage your subscription in Stripe.</p>
                        }
                        @if (surveyors.chargeable_activations_blocked) {
                            <p class="billing-error">New surveyor seats are blocked while a payment needs attention. Existing assignments remain usable. Use Manage billing, then refresh.</p>
                        }
                    </mat-card-content>
                </mat-card>
            </div>
        }

        <div class="billing-footer">
            <button mat-raised-button color="primary" type="button" (click)="manageBilling()" [disabled]="working || planState?.billing_managed_externally">Manage billing in Stripe</button>
        </div>
    }
</section>
